import TableWidgetSeparated from '@modul-connect/shared/components/molecules/tableWidget/tableWidgetSeparated.v2'
import { timespanOptions } from '../datePickers/timespanPickers/timespanPickers'
import ReportPage from '../reportPage.v2'
import { sensor_log_columns, vehicle_sensor_columns } from './temperatureReportDetails/log_columns'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon'
import { getVehicleIncidentInfo, getClimateControlIncidents, downloadClimateIncidentReportExcel } from '../../../state/actions/vehicles'
import { useAuth0 } from '@auth0/auth0-react'
import { IncidentDetailsTable } from './incidentDetailsTable'
import { ReactComponent as WarningIcon } from '@modul-connect/shared/img/ep_warning-filled.svg'
import { GroupingOptionSelector } from '../tollPassages/summary/groupingOptionSelector'
import { IncidentTemparatureChart } from './temperatureChartModal'
import { useTranslation } from 'react-i18next'
import { getServiceList } from '../../../utils/ServiceList'
import { ServiceType } from '@modul-connect/shared/utils/services'

export const groupingOptions = {
  onlyWarning: 'climateSystem.tempHistory.groupingOptions.onlyWarnings',
  all: 'climateSystem.tempHistory.groupingOptions.all'
}

const TemperatureHistory = ({ selected_organisations, subtrees }) => {
  
  const [startDate, setStartDate] = useState(DateTime.local().startOf('month'))
  const [prevStartDate, setPrevStartDate] = useState(startDate)
  const [endDate, setEndDate] = useState(DateTime.local().endOf('month'))
  const [prevEndDate, setPrevEndDate] = useState(endDate)
  const [mainboxesExpanded, setMainboxesExpanded] = useState([])
  const [fetchedIncidentsForDate, setFetchedIncidentsForDate] = useState([])
  const [show, setShow] = useState(groupingOptions.onlyWarning)
  const [selectedMainboxId, setSelectedMainboxId] = useState('')
  const [selectedStartDate, setSelectedStartDate] = useState(DateTime.local().startOf('month'))
  const [selectedEndDate, setSelectedEndDate] = useState(DateTime.local().endOf('month'))
  const [openModal, setOpenModal] = useState(false)

  const dispatch = useDispatch()

  const { getAccessTokenSilently } = useAuth0()

  const {t} = useTranslation()

  const startDateUTC = useMemo(() => startDate.toUTC(), [startDate.toSeconds()])
  const endDateUTC = useMemo(() => endDate.toUTC(), [endDate.toSeconds()])

  const selectedOrganisations = useSelector(state => state.selected_organisations)

  const disabled = !getServiceList({ subtrees }).hasServiceForSelected(selected_organisations, ServiceType.ClimateReports)

  useEffect(() => {
    setFetchedIncidentsForDate([])
    setPrevStartDate(startDate)
    setPrevEndDate(endDate)
    getAccessTokenSilently()
      .then(token => {
        dispatch(getVehicleIncidentInfo( token, startDateUTC, endDateUTC ))
      })
  }, [startDate.toSeconds(), endDate.toSeconds(), selectedOrganisations])

  useEffect(() => {

    const mainboxesNotFetched = startDate.equals(prevStartDate) && endDate.equals(prevEndDate) ? 
      mainboxesExpanded.filter(m => !fetchedIncidentsForDate.includes(m)) :
      mainboxesExpanded

    if (mainboxesNotFetched.length > 0)
      getAccessTokenSilently()
        .then(token => {
          dispatch(getClimateControlIncidents(token, mainboxesNotFetched.join(','), startDateUTC, endDateUTC))
        })
        .catch(err => console.error(err))
    setFetchedIncidentsForDate(prev => [...prev, ...mainboxesNotFetched])
  }, [startDate.toSeconds(), endDate.toSeconds(), mainboxesExpanded])

  const toggleMainbox = (mainboxId) => {
    setMainboxesExpanded(prev =>
      prev.includes(mainboxId)
        ? prev.filter(id => id !== mainboxId)  
        : [...prev, mainboxId]                
    )
  }

  const vehicleIncidentInfo = useSelector(state => state.vehicleIncidentInfo)

  const downloading = useSelector(state => state?.downloading)
  
  const incidentVehicles = useMemo(() => {

    return vehicleIncidentInfo.map(iv => {

      const collapsibleComponent = (
        <IncidentDetailsTable
          mainboxId={iv.mainboxId}
          columns={sensor_log_columns(t)}
          startDate={startDate}
          endDate={endDate}
          showSelection={show}
          setOpenModal={setOpenModal}
          setSelectedMainboxId={setSelectedMainboxId}
          setSelectedStartDate={setSelectedStartDate}
          setSelectedEndDate={setSelectedEndDate}
        />
      )

      return {
        id: iv.mainboxId,
        mainBoxId: iv.mainboxId,
        vehicleName: iv.vehicleName,
        numberOfIncidents: iv.numberOfIncidents,
        numberOfIncidents_icon: iv.numberOfIncidents > 0 ? <WarningIcon width={22} height={22}/> : null,
        isExpandable: true,
        collapsibleComponent,
        onExpand: () => toggleMainbox(iv.mainboxId),
        organisationIds: iv.organisationIds
      }
    })

  }, [vehicleIncidentInfo,startDate.toSeconds(), endDate.toSeconds(), show, selectedOrganisations,t])
  

  const vehicleTable = 
  <TableWidgetSeparated
    data={incidentVehicles}
    columns={vehicle_sensor_columns(t)}
    totalItems={incidentVehicles.length}
    themes={'themes'}
    isSortable
    defaultSort="vehicleName" 
    defaultSortOrder="asc"
    fixedLayout
  />

  const options = {
    all: groupingOptions.all,
    onlyWarning: groupingOptions.onlyWarning
    
  }

  const optionsLabels = {
    all: t(groupingOptions.all),
    onlyWarning: t(groupingOptions.onlyWarning)
  }

  const downloadIncidentReport = async () => {
    const accessToken = await getAccessTokenSilently()
    dispatch(downloadClimateIncidentReportExcel(accessToken, startDateUTC, endDateUTC))
  }

  return(
    <Fragment>
      <ReportPage 
        disabled={disabled}
        title={t('climateSystem.sidebar.temperatureHistory')}
        hasData={incidentVehicles?.some(i => i?.numberOfIncidents > 0)}
        noSearch
        timeSelectors={[
          timespanOptions.year,
          timespanOptions.month,
          timespanOptions.week,
          timespanOptions.day,
          timespanOptions.custom,
        ]}
        startDate={startDate}
        endDate={endDate}
        onTimeframePicked={(start, end) => {
          setStartDate(start)
          setEndDate(end)
        }}
        tables={vehicleTable}
        groupingOptionSelector={
          <GroupingOptionSelector
            onChange={(selection) => {
              console.log(selection)
              setShow(selection)
            }}
            options={options}
            labels={optionsLabels}
            value={show}
          />
        }
        onDownloadAll={() => downloadIncidentReport()}
        isPreparingFile={downloading?.incident_report?.status === 'working'}
        downloadFailed={downloading?.incident_report?.status === 'failed'}
      >
      </ReportPage>
      {openModal && (
        <div style={{display: 'flex', justifyContent: 'center'}}>
          <IncidentTemparatureChart 
            setOpenModal={setOpenModal} 
            openModal={openModal} 
            mainboxId={selectedMainboxId} 
            startDate={selectedStartDate.toUnixInteger()} 
            endDate={selectedEndDate.toUnixInteger()}
            title={t('climateSystem.tempHistory.tempChart')}
          />
        </div>
      )}

    </Fragment>
  )
}

const mapStateToProps = props => props

const mapDispatchToProps = () => ({
})

export default connect(mapStateToProps, mapDispatchToProps)(TemperatureHistory)